import { inject, Injectable } from "@angular/core";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { requireScopedProject } from "@domain/project/project-deserializer";
import type { ScopedProject } from "@domain/project/project-guards";
import { isFgdsProject, isMonitoringProject } from "@domain/project/project-guards";
import { BusinessScope } from "@lib/constants/business-scope";
import { ChecklistPagePdfService } from "@pdf/export-services/checklist-page/pdf-checklist.service";
import { PdfFloorplanImageService } from "@pdf/export-services/floorplan-images-page/pdf-floorplan-image.service";
import { LegendPagePdfService } from "@pdf/export-services/legend-page/legend-page-pdf.service";
import { PdfProjectInformationService } from "@pdf/export-services/project-information-page/pdf-project-information.service";
import { SignOffPagePdfService } from "@pdf/export-services/sign-off-page/sign-off-page-pdf.service";
import { PdfTableOfContentsService } from "@pdf/export-services/table-of-contents/pdf-table-of-contents.service";
import { TableOfContents } from "@pdf/export-services/table-of-contents/table-of-contents";
import { Headlines } from "@pdf/pdf-properties";
import { format } from "date-fns";
import { jsPDF } from "jspdf";
import { concatMap, from, Observable, of, reduce, switchMap } from "rxjs";
import { FloorplanPagePdfService } from "./export-services/floorplan-page/floorplan-page-pdf.service";
import { PdfFooterService } from "./export-services/footer/pdf-footer.service";
import { ProductInformationPagesService } from "./export-services/product-information-pages/product-information-pages.service";
import { TitlePagePdfService } from "./export-services/title-page/title-page-pdf.service";

type PdfStepData = { pdf: jsPDF; tableOfContents: any };

@Injectable({
  providedIn: "root",
})
export class PdfService {
  private titlePagePdfService = inject(TitlePagePdfService);
  private floorplanPagePdfService = inject(FloorplanPagePdfService);
  private legendPagePdfService = inject(LegendPagePdfService);
  private signOffPagePdfService = inject(SignOffPagePdfService);
  private productInformationPageService = inject(ProductInformationPagesService);
  private checklistPdfService = inject(ChecklistPagePdfService);
  private projectInformationService = inject(PdfProjectInformationService);
  private projectImageService = inject(PdfFloorplanImageService);
  private footerPdfService = inject(PdfFooterService);
  private tableOfContentsPdfService = inject(PdfTableOfContentsService);

  private readonly PPI = 300;
  private readonly WIDTH: number = this.calculateWidth(this.PPI);
  private readonly HEIGHT: number = this.calculateHeight(this.PPI);
  private readonly RESOLUTION = [this.WIDTH, this.HEIGHT];
  private readonly HEADLINES = new Headlines();

  exportProjectToPdf(
    project: ScopedProject,
    isRiskAssessmentIncluded: boolean,
    isSignOffPageIncluded: boolean = true,
  ): Observable<{ pdf: jsPDF; fileName: string }> {
    const { pdf, tableOfContents, exportTime, fileName } = this.setupPdf(project, project.name);
    const floorplanStep$ = from(project.cachedFloorplans ?? []).pipe(
      concatMap((floorplan) =>
        this.addFloorplanToPdf(pdf, floorplan, tableOfContents, (project.cachedFloorplans?.length ?? 0) > 1),
      ),
      reduce((_, result: PdfStepData) => result, { pdf, tableOfContents }),
    );
    return floorplanStep$.pipe(
      concatMap(() => this.generateProductListStep(pdf, project, tableOfContents)),
      concatMap(() => this.generateProjectInfoAndChecklistStep(pdf, project, tableOfContents, isRiskAssessmentIncluded)),
      concatMap(() => this.generateImagesStep(pdf, project)),
      concatMap(() => this.generateSignOffPageStep(pdf, project, tableOfContents, isSignOffPageIncluded)),
      concatMap(() => this.generateToCAndFooterStep(pdf, project, tableOfContents, fileName, exportTime)),
    );
  }

  exportFloorplanToPdf(floorplan: Floorplan): Observable<{ pdf: jsPDF; fileName: string }> {
    const project = requireScopedProject(floorplan.project);
    const { pdf, tableOfContents, exportTime, fileName } = this.setupPdf(project, floorplan.name);
    return this.addFloorplanToPdf(pdf, floorplan, tableOfContents).pipe(
      concatMap(({ pdf }) =>
        this.tableOfContentsPdfService.generate(pdf, tableOfContents).pipe(
          concatMap(({ pdf, numberOfAdditionalPagesNeeded, container }) => {
            pdf.setPage(2 + numberOfAdditionalPagesNeeded);
            this.legendPagePdfService.generate(pdf, container);
            this.footerPdfService.addToEachPage(pdf, project, fileName, exportTime);
            return of({ pdf, fileName });
          }),
        ),
      ),
    );
  }

  private setupPdf(project: ScopedProject, fileNamePrefix: string) {
    const pdf = new jsPDF({
      orientation: "l",
      unit: "px",
      format: this.RESOLUTION,
      putOnlyUsedFonts: true,
      compress: true,
    });
    const tableOfContents = new TableOfContents();
    tableOfContents.appendToTableOfContents("");
    tableOfContents.appendToTableOfContents(this.HEADLINES.LEGEND);

    const exportTime = new Date();
    const fileName = fileNamePrefix + "_" + this.formatDateForFileName(exportTime) + ".pdf";

    this.titlePagePdfService.generate(pdf, project, fileName, exportTime);
    pdf.addPage();
    return { pdf, tableOfContents, exportTime, fileName };
  }

  private addFloorplanToPdf(
    pdf: jsPDF,
    floorplan: Floorplan,
    tableOfContents: TableOfContents,
    shouldAddProductList = true,
  ): Observable<{ pdf: jsPDF; tableOfContents: TableOfContents }> {
    return new Observable<{ pdf: jsPDF; tableOfContents: TableOfContents }>((observer) => {
      const project = requireScopedProject(floorplan.project);
      pdf.addPage();
      this.floorplanPagePdfService
        .generate(pdf, floorplan)
        .pipe(
          switchMap(() => {
            tableOfContents.appendToTableOfContents(this.HEADLINES.FLOORPLAN + " | " + floorplan.name);
            return this.productInformationPageService.generateForFloorplan(
              pdf,
              floorplan,
              tableOfContents,
              shouldAddProductList,
              this.getScopeForProject(project),
            );
          }),
        )
        .subscribe({
          next: (result) => {
            observer.next(result);
            observer.complete();
          },
          error: (err) => {
            console.error(err);
            observer.error(err);
          },
        });
    });
  }

  private formatDateForFileName(date: Date) {
    return format(date, "yyyy-MM-dd_HHmmss");
  }

  private calculateWidth(ppi: number) {
    const inchInMm = 25.4;
    const a4WidthInMm = 297;
    return (a4WidthInMm / inchInMm) * ppi;
  }

  private calculateHeight(ppi: number) {
    const inchInMm = 25.4;
    const a4WHeightInMm = 210;
    return (a4WHeightInMm / inchInMm) * ppi;
  }

  private generateProductListStep(pdf: jsPDF, project: ScopedProject, tableOfContents: TableOfContents): Observable<jsPDF> {
    if (isFgdsProject(project)) {
      return this.productInformationPageService.generateFgdsProductList(pdf, project, tableOfContents);
    }
    if (isMonitoringProject(project)) {
      return this.productInformationPageService.generateMonitoringProductList(pdf, project, tableOfContents);
    }
    return of(pdf);
  }

  private generateProjectInfoAndChecklistStep(
    pdf: jsPDF,
    project: ScopedProject,
    tableOfContents: TableOfContents,
    isRiskAssessmentIncluded: boolean,
  ): Observable<jsPDF> {
    return this.projectInformationService.generate(pdf, project).pipe(
      concatMap(({ container }) => {
        if (isMonitoringProject(project)) {
          return this.checklistPdfService.generateMonitoring(pdf, project, tableOfContents, container, isRiskAssessmentIncluded);
        }
        if (isFgdsProject(project)) {
          return this.checklistPdfService.generateFgds(pdf, project, tableOfContents, container, isRiskAssessmentIncluded);
        }
        return of(pdf);
      }),
    );
  }

  private generateImagesStep(pdf: jsPDF, project: ScopedProject): Observable<jsPDF> {
    if (isMonitoringProject(project)) {
      return this.projectImageService.generateMonitoring(pdf, project);
    }
    return this.projectImageService.generate(pdf, project);
  }

  private generateSignOffPageStep(
    pdf: jsPDF,
    project: ScopedProject,
    tableOfContents: TableOfContents,
    isSignOffPageIncluded: boolean,
  ): Observable<jsPDF> {
    if (isMonitoringProject(project) && isSignOffPageIncluded) {
      return this.signOffPagePdfService.generate(pdf, tableOfContents, project);
    }
    return of(pdf);
  }

  private getScopeForProject(project: ScopedProject): BusinessScope {
    if (isMonitoringProject(project)) {
      return BusinessScope.MONITORING;
    }
    if (isFgdsProject(project)) {
      return BusinessScope.FGDS;
    }
    return BusinessScope.FGDS;
  }

  private generateToCAndFooterStep(
    pdf: jsPDF,
    project: ScopedProject,
    tableOfContents: TableOfContents,
    fileName: string,
    exportTime: Date,
  ): Observable<{ pdf: jsPDF; fileName: string }> {
    return this.tableOfContentsPdfService.generate(pdf, tableOfContents).pipe(
      concatMap(({ numberOfAdditionalPagesNeeded, container }) => {
        pdf.setPage(2 + numberOfAdditionalPagesNeeded);
        this.legendPagePdfService.generate(pdf, container);
        this.footerPdfService.addToEachPage(pdf, project, fileName, exportTime);
        return of({ pdf, fileName });
      }),
    );
  }
}
